import { ABtn } from '@maisaedu/ui-components-a-btn';

import { parseDate } from '@/helpers/date';
import { BANNER as BannerType, THUMBNAIL as ThumbnailType } from '@/helpers/contentDesign';
import { PROCESSING_STATUS } from '@/helpers/avaliaActivity.js';
import { SCORM_UPLOAD_STATUS } from '@/helpers/scorm';
import ContentType from '@/helpers/contentType';
import CategoryType from '@/helpers/contentCategory';
import TemplateType from '@/helpers/templateType';
import ConditionalTypes from '@/helpers/availabilityConditionalTypes';
import { getAutoGradeStatus, mapAutoGradeStatusChip } from '@/helpers/smartIntegrations';

import CourseMixin from '@/mixins/templates/course.js';
import CommonMixin from '@/mixins/templates/common.js';
import ColorAndIconMixin from '@/mixins/content-icon-color.js';
import ValidateTopicConfigurations from '@/mixins/validateTopicConfigurations';
import ContentSyncMixin from '@/mixins/content-sync';

import TopicSettingsMenu from '@/components/content-v2/templates/common/topic-settings-menu';
import TagStatus from '@/components/ui/tag-status';
import { getTopicSyncStatus } from '~/helpers/topicSyncStatus';
import { syncIconObject } from '@/helpers/contentSync';

export default {
  name  : 'CardTopicTimeline',
  props : {
    topic                        : Object,
    isStudent                    : Boolean,
    isCommunity                  : Boolean,
    isBlockedByParentConditional : {
      type    : Boolean,
      default : false
    },
    isPreview: {
      type    : Boolean,
      default : false
    }
  },
  mixins     : [ColorAndIconMixin, CourseMixin, CommonMixin, ValidateTopicConfigurations, ContentSyncMixin],
  components : { TopicSettingsMenu, ABtn, TagStatus },
  data() {
    return {
      PROCESSING_STATUS,
      SCORM_UPLOAD_STATUS,
      BannerType,
      ThumbnailType,
      CategoryType,
      ContentType,
      TemplateType,
      observer                  : null,
      hasZoom                   : false,
      dateConditionalBadgeTitle : '',
      autoGradeStatusMap        : mapAutoGradeStatusChip(),
      alertChipConfigs          : false
    };
  },
  mounted() {
    this.observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        this.$nextTick(() => {
          this.onClassChange(m.target.getAttribute(m.attributeName));
        });
      }
    });

    this.observer.observe(document.body, {
      attributes      : true,
      attributeFilter : ['class']
    });

    this.onClassChange(document.body.className);
  },
  computed: {
    isAvaliaAndHasRetriesLeft() {
      return this.isAvaliaExternalActivity(this.topic)
        && this.topic.content.hasRetries
        && this.topic.content.studentCurrentAttempt < this.topic.content.numberRetries;
    },
    shouldShowEnterContentButton() {
      if (!this.isAvaliaExternalActivity(this.topic)) {
        return this.$vuetify.breakpoint.mdAndUp || (this.isWebConference && this.$vuetify.breakpoint.mdAndUp);
      }

      if (this.$vuetify.breakpoint.smAndDown) {
        return false;
      }

      if (this.isAvaliaAndIsLockedByAccessCode && this.topicIsAvailableToAnswer(this.topic) && !this.isCompleted(this.topic)) {
        return true;
      }

      if (!this.topic.content.isActive || this.topic.content.status !== PROCESSING_STATUS.OK) {
        return true;
      }

      if (this.isTopicExpired(this.topic) && this.shouldShowResultActivity(this.topic)) {
        return true;
      }

      if (this.topicHasRetries(this.topic)) {
        if (this.studentAnsweredAllAttempts(this.topic)) {
          return this.shouldShowResultActivity(this.topic);
        }

        return !this.isTopicExpired(this.topic);
      }

      if (this.mustBeShownFeedback(this.topic)) {
        return (
          (this.isTopicFinished(this.topic) && this.shouldShowResultActivity(this.topic)) ||
          (!this.isTopicExpired(this.topic) && !this.isTopicFinished(this.topic))
        );
      }

      return this.$vuetify.breakpoint.mdAndUp && !this.isTopicExpired(this.topic) && !this.isTopicFinished(this.topic);
    },
    shouldShowEnterContentIcon() {
      const { topic } = this;
      if (!topic.content) {
        return true;
      }

      if (this.isAvaliaAndIsLockedByAccessCode && this.topicIsAvailableToAnswer(this.topic) && !this.isCompleted(this.topic)) {
        return true;
      }

      const { content } = topic;
      if (!content.hasEnded && content.isIntegrated) {
        return true;
      }

      if (topic.isFuture || this.isAvaliaAndInactive) {
        return true;
      }

      if (topic.categoryTypeId === CategoryType.SCORM && content.uploadStatus === SCORM_UPLOAD_STATUS.COMPLETE) {
        return true;
      }

      return false;

    },
    alwaysVisibleClassShouldShowEnterContentButton() {
      if (this.isAvaliaAndIsLockedByAccessCode && this.$vuetify.breakpoint.mdAndUp && !this.isCompleted(this.topic)) {
        return true;
      }

      if ((this.isWebConference && !this.isCompleted(this.topic)) || this.isAvaliaAndInactive) {
        return true;
      }

      if (this.isCommunity) {
        return true;
      }

      const topicDeadlineExpired = this.isTopicExpired(this.topic);
      if (this.isAvaliaExternalActivity(this.topic)) {
        if (!this.shouldShowResultActivity(this.topic)) {
          return this.topic.isFuture;
        } else if (this.topicHasRetries(this.topic)) {
          return this.studentAnsweredAllAttempts(this.topic);
        } else {
          return this.isTopicFinished(this.topic) || topicDeadlineExpired;
        }
      }

      return false;
    },
    computedProgress() {
      if (this.topic.childrenCountWithProgressRecord) {
        return parseFloat((this.topic.viewedChildrenCount / this.topic.childrenCountWithProgressRecord) * 100);
      } else {
        return this.topic.viewed ? 100 : 0;
      }
    },
    showDetails() {
      switch (this.topic.categoryTypeId) {
        case CategoryType.WEBCONFERENCE:
          return this.topic.content?.hasEnded || this.topic.content?.allowJoinWebconference;
        case CategoryType.LTI:
        case CategoryType.MENU_INTERACTIVES:
          return true;
        case CategoryType.SCORM:
          return !!this.topic.content.studentCurrentAttempt;
        default:
          return false;
      }
    },
    detailsLinkText() {
      if (this.hasWebConferenceProviderWithRecordings(this.topic)) {
        return this.$t('contentType.webconference.viewRecordings');
      }
      if (this.topic.categoryTypeId === CategoryType.SCORM) {
        return this.$t('contentType.scorm.attempts');
      }
      return this.isAvaliaExternalActivity(this.topic)
        ? this.$t('ltiLink.modalMoreInfo.informations')
        : this.$t('ltiLink.modalMoreInfo.moreInformation');
    },
    showAvailabilityBtn() {
      if (this.isStudent) {
        return this.topic.isBlockedContentByConditional || this.isBlockedByParentConditional;
      }
      return this.topic.hasConditional && this.topic.conditionals?.length > 0;
    },
    isActionDisabled() {
      if (this.isStudent && (this.topic.isBlockedContentByConditional || this.isBlockedByParentConditional)) {
        return true;
      }
      if (this.topic.categoryTypeId === CategoryType.WEBCONFERENCE) {
        return (
          !this.topic.content?.isIntegrated ||
          this.topic.content?.hasEnded ||
          (!this.topic.content?.hasEnded && !this.topic.content?.allowJoinWebconference)
        );
      }
      if (this.topic.categoryTypeId === CategoryType.AVALIA) {
        return (
          this.topic.isFuture || !this.topic.content.isActive || this.topic.content.status !== PROCESSING_STATUS.OK
        );
      }
      if (this.topic.categoryTypeId === CategoryType.SCORM) {
        return this.topic.content.uploadStatus !== SCORM_UPLOAD_STATUS.COMPLETE;
      }
      return (
        this.isStudent &&
        this.topic.categoryTypeId === CategoryType.ORGANIZATION &&
        (!this.topic.children || !this.topic.children.length)
      );
    },
    isModuleEmpty() {
      return this.topic.topicTypeId === ContentType.PAGE && (!this.topic.children || !this.topic.children.length);
    },
    isWConferenceNotIntegrated() {
      return this.topic.topicTypeId === ContentType.WEBCONFERENCE && !this.topic.content.isIntegrated;
    },
    isAvaliaAndInactive() {
      return (
        this.topic.topicTypeId === ContentType.AVALIA &&
        (!this.topic.content?.isActive || this.topic.content?.status !== PROCESSING_STATUS.OK)
      );
    },
    isAvaliaAndIsActive() {
      return (
        this.topic.topicTypeId === ContentType.AVALIA &&
        this.topic.content.isActive &&
        this.topic.content.status === PROCESSING_STATUS.OK
      );
    },
    isAvaliaAndIsLockedByAccessCode() {
      return this.topic.topicTypeId === ContentType.AVALIA && this.topic.isAccessCodeEnabled;
    },
    hasAutoGrade() {
      if (this.topic.topicTypeId !== ContentType.TASK) {
        return false;
      }

      return this.topic.hasAutoGrade;
    },
    autoGradeStatusChip() {
      const autoGradeResponse = this.topic.content?.autoGradeResponse;
      const autoGradeStatus = autoGradeResponse?.status;

      return getAutoGradeStatus({
        autoGradeResponse,
        autoGradeStatus,
        tooltipPropName       : 'tooltipMessage',
        autoGradeStatusMap    : this.autoGradeStatusMap,
        getterAutoGradeStatus : this.getterAutoGradeStatus
      });
    },
    statusChip() {
      if (this.isStudent) {
        return;
      }

      if (this.hasAutoGrade) {
        return this.autoGradeStatusChip;
      }

      if (this.isAvaliaAndInactive && this.topic.content?.status) {
        return {
          tooltipMessage : this.topic.content.status === PROCESSING_STATUS.OK ? this.$t('contentType.avalia.textTooltipTopicAvaliaInactive') : this.$t('contentType.avalia.textTooltipTopicAvaliaProcessing'),
          text           : this.topic.content.status === PROCESSING_STATUS.OK ? this.$t('content.cardTopic.avaliaInactive') : this.$t('content.cardTopic.pendingUpdate'),
          color          : '',
          icon           : this.topic.content.status !== PROCESSING_STATUS.OK ? 'mdi-progress-clock' : '',
          type           : this.topic.content.status === PROCESSING_STATUS.OK ? 'inactive' : 'warning'
        };
      }

      if (this.isWConferenceNotIntegrated) {
        return {
          tooltipMessage : this.$t('contentType.scorm.textTooltipTopicError'),
          text           : this.$t('contentType.scorm.tagStatusTextError'),
          icon           : 'mdi-alert',
          type           : 'error'
        };
      }
    },
    isWebConference() {
      return this.topic.categoryTypeId === CategoryType.WEBCONFERENCE;
    },
    shouldShowCompletedTopicIcon() {
      if (!this.isCommunity) {
        if (this.isAvaliaExternalActivity(this.topic)) {
          if (this.isAvaliaAndInactive) {
            return false;
          }

          if (this.topicHasRetries(this.topic)) {
            if (this.isTopicExpired(this.topic)) {
              return (
                !this.topic.isFuture &&
                this.studentAnsweredAllAttempts(this.topic) &&
                !this.shouldShowResultActivity(this.topic)
              );
            }

            if (this.shouldShowResultActivity(this.topic) && this.isCompleted(this.topic)) {
              return true;
            }
            return !this.topic.isFuture && this.topic.isRecordProgress;
          }

          if (this.topic.isBeforeDeadline) {
            if (this.shouldShowResultActivity(this.topic)) {
              return !this.isTopicFinished(this.topic);
            }
            return this.topic.isRecordProgress && !this.topic.isFuture;
          } else {
            return false;
          }
        }

        if (
          this.topic.categoryTypeId !== CategoryType.WEBCONFERENCE &&
          this.topic.categoryTypeId !== CategoryType.ORGANIZATION &&
          this.topic.isRecordProgress
        ) {
          return true;
        }

        if (this.isWebConference && this.topic.viewed && this.topic.content.hasEnded) {
          return true;
        }

        if (this.topic.topicTypeId === ContentType.PAGE) {
          return true;
        }
      }

      return false;
    },
    shouldShowEnterContentButtonMobile() {
      if (this.$vuetify.breakpoint.mdAndUp || !this.isStudent) {
        return false;
      }

      if (this.isAvaliaExternalActivity(this.topic)) {
        return this.topic.isBeforeDeadline && !this.isAvaliaAndIsLockedByAccessCode;
      }

      return !this.topic.isBlockedContentByConditional && !this.isBlockedByParentConditional;
    },
    shouldShowStudentProgressBarMobile() {
      if (!this.isCommunity && this.isStudent && this.topic.isRecordProgress) {
        if (this.topic.categoryTypeId === CategoryType.ORGANIZATION) {
          return this.topic.children;
        }

        if (this.isAvaliaAndInactive) {
          return false;
        }

        return this.topic.categoryTypeId !== CategoryType.ORGANIZATION;
      }

      return false;
    },
    shouldShowNoContentFlag() {
      return (
        this.topic.topicTypeId === ContentType.PAGE &&
        !this.isStudent &&
        (!this.topic.hasOwnProperty('children') || !this.topic.children || !this.topic.children.length)
      );
    },
    shouldShowTopicInformationsContainer() {
      if (this.topic.categoryTypeId === CategoryType.AVALIA) {
        return this.topic.content?.status === PROCESSING_STATUS.OK && this.topic.content?.isActive;
      } else if (this.topic.categoryTypeId === CategoryType.ORGANIZATION) {
        return this.topic.children;
      }

      return (
        this.isWebConference ||
        this.showDetails ||
        this.topic.totalNotCorrected ||
        this.topic.hasDeadline ||
        this.topic.hasGrade
      );
    },
    shouldShowTopicInformationsContainerMobile() {
      if (!this.isStudent) {
        if (
          this.topic.categoryTypeId === CategoryType.SCORM &&
          (this.topic.content.uploadStatus === SCORM_UPLOAD_STATUS.ERROR ||
            this.topic.content.uploadStatus === SCORM_UPLOAD_STATUS.RUNNING)
        ) {
          return true;
        }

        return this.statusChip;
      }

      return (
        this.isWebConference ||
        this.showDetails ||
        this.topic.totalNotCorrected ||
        this.topic.hasDeadline ||
        this.topic.hasGrade ||
        this.topic.expiredChildrenCount ||
        this.showAvailabilityBtn
      );
    },
    shouldShowAvaliaActivityResultsButtonMobile() {
      if (this.$vuetify.breakpoint.smAndDown) {
        return this.isStudent
         && this.isAvaliaExternalActivity(this.topic)
         && (this.shouldShowResultActivity(this.topic)
          && (this.studentAnsweredAllAttempts(this.topic) || this.isTopicDeadlineExpired(this.topic)));
      }

      return false;
    },
    shouldShowAvaliaActivityUnlockButtonMobile() {
      return this.isAvaliaAndIsLockedByAccessCode
        && !this.shouldShowAvaliaActivityResultsButtonMobile
        && !this.studentAnsweredAllAttempts(this.topic)
        && !this.isTopicExpiredAndNotMustBeShownFeedback(this.topic);
    },
    topicSyncIconObject() {
      let syncStatus = null;

      if (this.isMasterCourse && this.topic && this.getterCourse.associatedAcademicMains) {
        syncStatus = getTopicSyncStatus({
          topic                   : this.topic,
          topicCategoryType       : this.topic.categoryTypeId,
          associatedAcademicMains : this.getterCourse.associatedAcademicMains
        });
      }

      return syncStatus ? syncIconObject[syncStatus] : null;
    }
  },
  methods: {
    getType(categoryTypeId) {
      return CategoryType.getCategoryTypeNameById(categoryTypeId);
    },
    getColorByTopicTypeId() {
      if (this.topicIsHidden(this.topic)) {
        return 'var(--v-neutrals-300-base)';
      } else if (this.topic.design.cardDetailsColor) {
        return this.topic.design.cardDetailsColor;
      } else {
        return this.getColor(this.topic.topicTypeId);
      }
    },
    getIconByRequestOrByDefault(topic) {
      return topic.design.thumbnailDesignId === ThumbnailType.ICON && topic.design.thumbnailValue
        ? topic.design.thumbnailValue
        : this.getIcon(topic.topicTypeId);
    },
    getBtnEnterContentColor(topic) {
      if (topic.categoryTypeId === CategoryType.AVALIA) {
        if (this.isTopicExpired(this.topic) && this.shouldShowResultActivity(this.topic)) {
          return '#20a553';
        }

        if (
          this.shouldShowResultActivity(this.topic) &&
          ((this.topicHasRetries(this.topic) && this.studentAnsweredAllAttempts(this.topic)) ||
            (!this.topicHasRetries(this.topic) && this.isTopicFinished(this.topic)))
        ) {
          return '#20a553';
        }

        return '#255b98';
      } else {
        return '#255b98';
      }
    },
    getIconEnterContent(topic) {
      if (
        (topic.categoryTypeId === CategoryType.WEBCONFERENCE && !topic.content.allowJoinWebconference) ||
        (topic.categoryTypeId === CategoryType.AVALIA &&
          (topic.isFuture || !topic.content?.isActive || topic.content.status !== PROCESSING_STATUS.OK))
      ) {
        return 'mdi-lock';
      }

      if (this.isAvaliaAndIsLockedByAccessCode) {
        return 'mdi-key';
      }

      return 'mdi-chevron-right';
    },
    getIconEnterContentMobile(topic) {
      if (
        (topic.categoryTypeId === CategoryType.ORGANIZATION && (!topic.children || !topic.children.length)) ||
        (topic.categoryTypeId === CategoryType.WEBCONFERENCE &&
          !topic.content.hasEnded &&
          !topic.content.allowJoinWebconference) ||
        (topic.categoryTypeId === CategoryType.AVALIA &&
          (topic.isFuture || !topic.content?.isActive || topic.content.status !== PROCESSING_STATUS.OK))
      ) {
        return 'mdi-lock';
      } else if (topic.categoryTypeId === CategoryType.WEBCONFERENCE && topic.content.hasEnded) {
        return 'mdi-calendar-remove-outline';
      }
      return 'mdi-chevron-right';
    },
    getTextEnterContent(topic) {
      if (this.isCommunity) {
        return this.$t('content.cardTopic.access');
      }
      switch (topic.categoryTypeId) {
        case CategoryType.ORGANIZATION:
          if (topic.children && topic.children.filter(children => children.viewed).length) {
            return this.$t('content.cardTopic.continue');
          } else {
            return this.$t('content.cardTopic.start');
          }
        case CategoryType.WEBCONFERENCE:
          if (!topic.content.isIntegrated) {
            return this.$t('contentType.webconference.linkSoon');
          } else if (topic.content.hasEnded) {
            return this.$t('contentType.webconference.ended');
          } else if (topic.content.allowJoinWebconference) {
            return this.$t('contentType.webconference.access');
          }
          return this.$t('contentType.webconference.soon');
        case CategoryType.LTI:
          if (topic.viewed) {
            return this.$t('content.cardTopic.access');
          } else {
            return this.$t('content.cardTopic.start');
          }
        case CategoryType.AVALIA:
          if (this.isAvaliaAndIsLockedByAccessCode && this.topicIsAvailableToAnswer(this.topic) && !this.studentAnsweredAllAttempts(this.topic)) {
            return this.$t('contentType.avalia.unlock');
          }

          if (topic.isFuture || !topic.content.isActive || topic.content.status !== PROCESSING_STATUS.OK) {
            return this.$t('content.cardTopic.comingSoon');
          }

          if (this.isTopicExpired(this.topic) && this.shouldShowResultActivity(this.topic)) {
            return this.$t('content.cardTopic.result');
          }

          if (
            this.shouldShowResultActivity(this.topic) &&
            ((this.topicHasRetries(this.topic) && this.studentAnsweredAllAttempts(this.topic)) ||
              (!this.topicHasRetries(this.topic) && this.isTopicFinished(this.topic)))
          ) {
            return this.$t('content.cardTopic.result');
          }

          if (topic.content.hasRetries) {
            if (topic.content.studentCurrentAttempt >= topic.content.numberRetries) {
              return this.$t('content.cardTopic.result');
            }

            return this.$tc('content.cardTopic.startAttempt', topic.content.studentCurrentAttempt + 1, {
              attemptNumber: topic.content.studentCurrentAttempt + 1
            });
          }

          return this.$t('content.cardTopic.start');
        case CategoryType.SCORM:
          if (topic.content.uploadStatus !== SCORM_UPLOAD_STATUS.COMPLETE) {
            return this.$t('content.cardTopic.comingSoon');
          } else if (this.isTopicDeadlineExpired(this.topic)) {
            return this.$t('content.cardTopic.access');
          } else if (topic.content.studentCurrentAttempt >= 1) {
            return this.$t('contentType.scorm.newAttempt');
          }
          return this.$t('content.cardTopic.start');
        default:
          if (topic.viewed) {
            return this.$t('content.cardTopic.continue');
          } else {
            return this.$t('content.cardTopic.start');
          }
      }
    },
    handleShowDetails() {
      this.$emit('emitOpenModalDetails', this.topic);
    },
    onClassChange(classList) {
      if (classList?.includes('INDmagnifier')) {
        this.hasZoom = true;
      } else {
        this.hasZoom = false;
      }
    },
    getIconModalTopicDetails() {
      if (this.hasWebConferenceProviderWithRecordings(this.topic)) {
        return 'mdi-filmstrip';
      }
      if (this.topic.categoryTypeId === CategoryType.SCORM) {
        return 'mdi-bullseye-arrow';
      }

      return 'mdi-text-box-plus-outline';

    },
    getTopicDateConditional() {
      const dateConditional = this.topic.conditionals?.find(
        conditional =>
          conditional.typeId === ConditionalTypes.DATE_MANUAL ||
          conditional.typeId === ConditionalTypes.DATE_ACADEMIC_MAIN
      );
      if (!dateConditional) {
        return '-';
      }

      if (dateConditional.typeId === ConditionalTypes.DATE_MANUAL) {
        const startDate = parseDate(dateConditional.startAt);
        const endDate = parseDate(dateConditional.endAt);

        if (startDate && endDate) {
          this.dateConditionalBadgeTitle = '';
          return `${startDate} ${this.$t('common.until')} ${endDate}`;
        }

        if (startDate) {
          this.dateConditionalBadgeTitle = this.$t('content.cardTopic.comingSoon');
          return startDate;
        }

        if (endDate) {
          this.dateConditionalBadgeTitle = this.$t('content.cardTopic.visibleUntil');
          return endDate;
        }
      }

      return '-';
    },
    openAvaliaActivityResults(event, topic) {
      if (this.avaliaActivityAttemptsIsLinkEnabled(topic)) {
        event.stopPropagation();

        const params = {
          destinationPage                  : 'results',
          classExternalId                  : this.getterCourse.context.academicMainExternalId,
          activityExternalSource           : topic.content.activityExternalSource,
          questionCollectionExternalSource : topic.content.questionCollectionExternalSource,
          activityExternalId               : topic.content.activityExternalId,
          questionCollectionExternalId     : topic.content.questionCollectionExternalId
        };

        this.callAvaliaSSO(params, topic);
      }
    }
  },
  beforeDestroy() {
    this.observer?.disconnect();
  }
};
